import { useEffect, useMemo, useState } from "react";
import { Alert } from "react-bootstrap";
import { useHistory, useLocation } from "react-router-dom";

import {
  useGetRallyById,
  useGetRallyCompetitors,
  useGetRallyStages,
  useGetRallies,
} from "../../../../../api/jeep-rally/rally/rally.hooks";
import { useSetRankingsMutation } from "../../../../../api/jeep-rally/rankings/rankings.hooks";
import { useGetAllCategories } from "../../../../../api/jeep-rally/categories/categories.hooks";
import {
  isJeepRallyAdmin,
  normalizeCategories,
} from "../../Categories/utils/category.utils";
import {
  getCategorySelectValue,
  getRallyCategories,
  normalizeRallies,
  normalizeRally,
} from "../../Rallies/utils/rally.utils";
import RankingForm from "../_components/RankingForm";
import {
  buildRankingPayload,
  buildRankingSheetRows,
  buildSampleRankingRows,
  downloadRankingsWorkbook,
  emptyRankingRow,
  getRankingApiErrorMessage,
  initialRankingForm,
  mapImportedRowsToRankings,
  normalizeCompetitors,
  normalizeStages,
  parseRankingsImportFile,
  validateRankingForm,
} from "../utils/ranking.utils";

const RankingCreatePage = () => {
  const history = useHistory();
  const location = useLocation();
  const canManage = isJeepRallyAdmin();

  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const eventIdFromQuery = query.get("eventId") || "";
  const categoryFromQuery = query.get("category") || "";
  const stageFromQuery = query.get("after_stage") || "";

  const [eventId, setEventId] = useState(eventIdFromQuery);
  const [form, setForm] = useState(() => ({
    ...initialRankingForm,
    category: categoryFromQuery,
    after_stage: stageFromQuery,
    rankings: [emptyRankingRow()],
  }));
  const [validationErrors, setValidationErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [importMessage, setImportMessage] = useState("");
  const [importError, setImportError] = useState("");

  const { data: ralliesData, isLoading: isRalliesLoading } = useGetRallies();
  const rallies = useMemo(
    () => normalizeRallies(ralliesData),
    [ralliesData]
  );

  useEffect(() => {
    if (eventIdFromQuery) {
      setEventId(eventIdFromQuery);
    }
  }, [eventIdFromQuery]);

  const {
    data: rallyData,
    isLoading: isRallyLoading,
  } = useGetRallyById(eventId);

  const { data: allCategoriesData, isLoading: isAllCategoriesLoading } =
    useGetAllCategories();

  const allCategories = useMemo(
    () => normalizeCategories(allCategoriesData),
    [allCategoriesData]
  );

  const rally = useMemo(
    () => normalizeRally(rallyData),
    [rallyData]
  );

  const categories = useMemo(() => {
    const rallyCats = getRallyCategories(rally);
    if (!rallyCats.length) return allCategories;

    return rallyCats
      .map((category) => {
        if (category && typeof category === "object" && category.key) {
          return category;
        }
        const key =
          typeof category === "string"
            ? category
            : getCategorySelectValue(category);
        const match = allCategories.find(
          (item) =>
            item.key === key || item._id === key || item.id === key
        );
        return match || { key, title: key };
      })
      .filter((category) => category?.key);
  }, [rally, allCategories]);

  const {
    data: stagesData,
    isLoading: isStagesLoading,
  } = useGetRallyStages(eventId, {
    enabled: Boolean(eventId),
  });

  const stages = useMemo(
    () => normalizeStages(stagesData),
    [stagesData]
  );

  const {
    data: competitorsData,
    isLoading: isCompetitorsLoading,
  } = useGetRallyCompetitors(
    eventId,
    { category: form.category },
    { enabled: Boolean(eventId) && Boolean(form.category) }
  );

  const competitors = useMemo(
    () => normalizeCompetitors(competitorsData),
    [competitorsData]
  );

  const {
    mutateAsync: setRankings,
    isLoading,
    isPending,
    reset,
  } = useSetRankingsMutation();

  const submitting = isLoading || isPending;
  const rallyName =
    rally?.name ||
    rallies.find((item) => (item._id || item.id) === eventId)?.name ||
    "";

  const fileBaseName = [
    "rankings",
    rallyName || "rally",
    form.category || "category",
    form.after_stage !== "" ? `stage-${form.after_stage}` : null,
  ]
    .filter(Boolean)
    .join("-")
    .replace(/[^a-z0-9-_]+/gi, "-")
    .toLowerCase();

  const updateField = (key, value) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "category") {
        next.rankings = (prev.rankings || []).map((row) => ({
          ...row,
          team_id: "",
        }));
      }
      return next;
    });
    setValidationErrors((prev) => ({ ...prev, [key]: undefined }));
    setImportMessage("");
    setImportError("");
  };

  const updateRow = (index, patch) => {
    setForm((prev) => {
      const rankings = [...(prev.rankings || [])];
      rankings[index] = { ...rankings[index], ...patch };
      return { ...prev, rankings };
    });
    setValidationErrors((prev) => {
      if (!prev.rows?.[index]) return prev;
      const nextRows = { ...prev.rows };
      nextRows[index] = { ...nextRows[index] };
      Object.keys(patch).forEach((key) => {
        delete nextRows[index][key];
      });
      if (!Object.keys(nextRows[index]).length) delete nextRows[index];
      return { ...prev, rows: nextRows };
    });
  };

  const addRow = () => {
    setForm((prev) => ({
      ...prev,
      rankings: [...(prev.rankings || []), emptyRankingRow()],
    }));
  };

  const removeRow = (index) => {
    setForm((prev) => {
      const rankings = (prev.rankings || []).filter((_, i) => i !== index);
      return {
        ...prev,
        rankings: rankings.length ? rankings : [emptyRankingRow()],
      };
    });
  };

  const handleDownloadSample = () => {
    downloadRankingsWorkbook({
      rows: buildSampleRankingRows(competitors),
      filename: `${fileBaseName}-sample`,
      format: "xlsx",
    });
  };

  const handleExport = () => {
    downloadRankingsWorkbook({
      rows: buildRankingSheetRows(form.rankings, competitors),
      filename: fileBaseName,
      format: "xlsx",
    });
  };

  const handleImportFile = async (file) => {
    setImportMessage("");
    setImportError("");
    setValidationErrors((prev) => ({
      ...prev,
      rankings: undefined,
      rows: undefined,
    }));

    try {
      const importedRows = await parseRankingsImportFile(file);
      const { rankings, warnings } = mapImportedRowsToRankings(
        importedRows,
        competitors
      );

      if (!rankings.length) {
        setImportError(
          "No ranking rows found in the file. Use the sample template headers."
        );
        return;
      }

      setForm((prev) => ({
        ...prev,
        rankings,
      }));

      if (warnings.length) {
        setImportError(warnings.slice(0, 5).join(" "));
      }
      setImportMessage(
        `Imported ${rankings.length} row${rankings.length === 1 ? "" : "s"} from ${file.name}. Review and edit the table before saving.`
      );
    } catch (error) {
      setImportError(
        error?.message || "Failed to import file. Use .xlsx or .csv."
      );
    }
  };

  const goBack = () => {
    const search = new URLSearchParams();
    if (eventId) search.set("eventId", eventId);
    if (form.category) search.set("category", form.category);
    if (form.after_stage !== "") search.set("after_stage", form.after_stage);
    const queryString = search.toString();
    history.push(
      queryString
        ? `/jeep-rally/ranking?${queryString}`
        : "/jeep-rally/ranking"
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canManage || !eventId) return;

    reset();
    setSubmitError("");
    setImportMessage("");

    const nextErrors = validateRankingForm(form);
    setValidationErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    try {
      await setRankings({
        eventId,
        payload: buildRankingPayload(form),
      });
      const search = new URLSearchParams({
        eventId,
        category: form.category,
        after_stage: String(form.after_stage),
      });
      history.push(`/jeep-rally/ranking?${search.toString()}`);
    } catch (error) {
      setSubmitError(
        getRankingApiErrorMessage(error, "Failed to save rankings.")
      );
    }
  };

  if (!eventId) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-header">
            <h4 className="card-title mb-0">Add rankings</h4>
          </div>
          <div className="card-body">
            <Alert variant="warning" className="mb-3">
              Select a rally on the rankings list first, then click Add
              rankings.
            </Alert>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => history.push("/jeep-rally/ranking")}
            >
              Back to rankings
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-link p-0 text-decoration-none"
              onClick={goBack}
              aria-label="Back to rankings list"
              title="Back"
            >
              <i className="bi bi-arrow-left fs-4" />
            </button>
            <h4 className="card-title mb-0">Add rankings</h4>
          </div>
        </div>

        <div className="card-body">
          {!canManage && (
            <Alert variant="warning" className="mb-4">
              Admin access is required to set rankings.
            </Alert>
          )}

          {submitError && (
            <Alert variant="danger" className="mb-4">
              {submitError}
            </Alert>
          )}

          {(isRalliesLoading || isRallyLoading) && !rallyName ? (
            <p className="text-muted">Loading rally...</p>
          ) : null}

          <RankingForm
            form={form}
            validationErrors={validationErrors}
            submitting={submitting}
            readOnly={!canManage}
            categories={categories}
            isCategoriesLoading={
              isRallyLoading || isAllCategoriesLoading
            }
            stages={stages}
            isStagesLoading={isStagesLoading}
            competitors={competitors}
            isCompetitorsLoading={isCompetitorsLoading}
            rallyName={rallyName}
            importMessage={importMessage}
            importError={importError}
            submitLabel="Save rankings"
            submittingLabel="Saving..."
            onSubmit={handleSubmit}
            onFieldChange={updateField}
            onRowChange={updateRow}
            onAddRow={addRow}
            onRemoveRow={removeRow}
            onDownloadSample={handleDownloadSample}
            onExport={handleExport}
            onImportFile={handleImportFile}
            onBack={goBack}
          />
        </div>
      </div>
    </div>
  );
};

export default RankingCreatePage;
